import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Icon,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiFileText,
  FiX,
  FiClock,
  FiCheckCircle,
  FiTrendingUp,
  FiUsers,
  FiDollarSign,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import routes from '../routes';
import UserService from '../utils/UserService';
import { useSelector } from 'react-redux';

const TermsheetNotification = ({
  isOpen,
  onClose,
  setShouldShowNotification,
}) => {
  const navigate = useNavigate();
  const accountType = useSelector(
    (state) => state?.user?.userInfo?.userTypeTitle,
  );

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('blue.500', 'blue.300');

  const handleCreateDealFlow = () => {
    onClose();
    navigate('/deal-flow');
  };

  const handleSnooze = () => {
    setShouldShowNotification(false);
    UserService.setTermsheetNotificationSnoozed();
    onClose();
  };

  // const handleDismiss = () => {
  //   onClose();
  //   UserService.setTermsheetNotificationDismissed(true);
  // };

  const location = useLocation();

  const isPublicPage = routes.publicRoutes.some(
    (route) => `/${route.path}` === location.pathname,
  );

  const founderRoutes =
    accountType === 'founder'
      ? routes.foundersRoutes.map((route) => route.path)
      : [];

  const isAdminPage = routes.adminRoutes
    .filter((route) => route.path && !founderRoutes.includes(route.path))
    .some((route) => `/${route.path}` === location.pathname);

  const isDealFlowPage = location.pathname === '/deal-flow';

  const isSurveyPage = location.pathname === '/survey';
  const showModal =
    isOpen && !isDealFlowPage && !isPublicPage && !isAdminPage && !isSurveyPage;

  return (
    <Modal
      isOpen={showModal}
      onClose={onClose}
      isCentered
      closeOnOverlayClick={false}
      size="md"
      data-notification-type="deal-flow"
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        bg={bgColor}
        borderRadius="xl"
        boxShadow="xl"
        border="1px solid"
        borderColor={borderColor}
        mx={4}
      >
        <ModalBody p={6}>
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <Flex justify="space-between" align="center">
              <HStack spacing={3}>
                <Box p={2} borderRadius="full" bg="green.50" color="green.600">
                  <Icon as={FiDollarSign} boxSize={5} />
                </Box>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  Start Your Fundraising Journey
                </Text>
              </HStack>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                color="gray.500"
                _hover={{ color: 'gray.700' }}
              >
                <Icon as={FiX} boxSize={4} />
              </Button>
            </Flex>

            {/* Content */}
            <VStack spacing={4} align="stretch">
              <Text color="gray.600" lineHeight="tall">
                Create your first deal flow to showcase your startup to
                investors and begin your fundraising process. This is your
                gateway to connecting with potential investors.
              </Text>

              {/* Benefits */}
              <Box
                bg="green.50"
                p={4}
                borderRadius="lg"
                border="1px solid"
                borderColor="green.100"
              >
                <VStack spacing={3} align="stretch">
                  <HStack spacing={3}>
                    <Icon as={FiUsers} color="green.600" boxSize={4} />
                    <Text fontSize="sm" color="green.800" fontWeight="medium">
                      Connect with verified investors
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon as={FiTrendingUp} color="green.600" boxSize={4} />
                    <Text fontSize="sm" color="green.800" fontWeight="medium">
                      Increase visibility in investor network
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon as={FiFileText} color="green.600" boxSize={4} />
                    <Text fontSize="sm" color="green.800" fontWeight="medium">
                      Professional deal presentation
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Icon as={FiCheckCircle} color="green.600" boxSize={4} />
                    <Text fontSize="sm" color="green.800" fontWeight="medium">
                      Streamlined fundraising process
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </VStack>

            {/* Action Buttons */}
            <VStack spacing={3} pt={2}>
              <Button
                style={{ backgroundColor: '#134A70', color: 'white' }}
                size="lg"
                width="full"
                onClick={handleCreateDealFlow}
                loadingText="Loading..."
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'lg',
                }}
                transition="all 0.2s"
              >
                Create Deal Flow
              </Button>

              <HStack spacing={3} width="full">
                <Button
                  variant="outline"
                  size="md"
                  flex={1}
                  onClick={handleSnooze}
                  leftIcon={<Icon as={FiClock} />}
                  color="gray.600"
                  borderColor="gray.300"
                  _hover={{
                    bg: 'gray.50',
                    borderColor: 'gray.400',
                  }}
                >
                  Remind in 30 days
                </Button>
                {/* <Button
                  variant="ghost"
                  size="md"
                  onClick={handleDismiss}
                  color="gray.500"
                  _hover={{
                    color: 'gray.700',
                    bg: 'gray.100',
                  }}
                >
                  Don't show again
                </Button> */}
              </HStack>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TermsheetNotification;
